import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RefreshCw } from 'lucide-react';
import { xhrJson } from '../outcomeApi';
import { PeriodPicker, presetRange } from './PeriodPicker';
import { ChipMultiSelect, REPORT_OUTCOMES, DEFAULT_REPORT_OUTCOMES, OUTCOME_COLORS } from './reportShared';
import { SummaryTiles, DailyChart, BreakdownTable } from './ReportPanels';
import { InteractionList } from './InteractionList';

export function ReengagementReport({ refreshKey = 0 }) {
  const [period, setPeriod] = useState({ preset: 'last_7', ...presetRange('last_7') });
  const [outcomes, setOutcomes] = useState(DEFAULT_REPORT_OUTCOMES);
  const [admins, setAdmins] = useState(null); // null = everyone
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!period.start || !period.end || !outcomes.length || (admins && !admins.length)) return;
    const qs = new URLSearchParams({ start: period.start, end: period.end, outcomes: outcomes.join(',') });
    if (admins && admins.length) qs.set('admins', admins.join(','));
    setLoading(true); setError('');
    xhrJson('GET', `/admin/reengagement/report?${qs}`, null, (s, d) => {
      setLoading(false);
      if (s === 200) setData(d); else setError(d?.detail || 'Failed to load report');
    });
  }, [period, outcomes, admins, refreshKey]);

  const team = (data?.team || []).map(t => ({ key: t.email, label: t.name }));
  const outcomeOpts = REPORT_OUTCOMES.map(o => ({ ...o, color: OUTCOME_COLORS[o.key] }));
  const activeKeys = outcomes.filter(k => data?.totals?.by_outcome?.[k]);
  const noneSelected = !outcomes.length || (admins && !admins.length);

  return (
    <div className="space-y-4" data-testid="reengagement-report">
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <PeriodPicker {...period} onChange={setPeriod} />
          {loading && <RefreshCw className="w-4 h-4 animate-spin text-orange-500" data-testid="report-loading" />}
        </div>
        <div>
          <Label className="text-xs">Outcomes</Label>
          <div className="mt-1"><ChipMultiSelect options={outcomeOpts} selected={outcomes} onChange={setOutcomes} testPrefix="report-outcome" /></div>
        </div>
        {team.length > 0 && (
          <div>
            <Label className="text-xs">Team member</Label>
            <div className="mt-1">
              <ChipMultiSelect options={team} selected={admins ?? team.map(t => t.key)} onChange={setAdmins} testPrefix="report-admin" />
            </div>
          </div>
        )}
      </Card>

      {error && <div className="text-sm text-red-600" data-testid="report-error">{error}</div>}
      {noneSelected && <div className="text-sm text-muted-foreground" data-testid="report-none-selected">Pick at least one outcome and team member.</div>}
      {data && !noneSelected && (
        <>
          <SummaryTiles totals={data.totals} byAdmin={data.by_admin} />
          <BreakdownTable title="By team member" rows={data.by_admin} outcomeKeys={activeKeys} labelKey="name" testId="report-by-admin" />
          <DailyChart daily={data.daily} outcomeKeys={activeKeys} />
          <BreakdownTable title="Day by day" rows={[...data.daily].reverse()} outcomeKeys={activeKeys} labelKey="date" testId="report-by-day" />
          <InteractionList items={data.items} truncated={data.items_truncated} />
        </>
      )}
    </div>
  );
}
